/**
 * PATH: packages/ui/src/kanban/components/useKanbanCardInteractions.ts
 * FILE_ID: UI-KANBAN-CARD-INTERACTIONS-001
 * PURPOSE: Owns every pointer/drag/touch interaction for a KanbanCard root —
 *   the hover / chromeArmed / dragging / email-drop / nest-drop / just-allocated /
 *   hover-dwell state plus the root element's event handlers.
 * WHY: Lifted from KanbanCard.tsx so the component stays a thin orchestrator.
 *   FEAT-2026-07-24-KANBAN-HOVER-CHROME-DWELL: chrome (lift / action zone /
 *   strip / note) waits CHROME_DWELL_MS and is suppressed while lanes scroll;
 *   hotkeys keep using immediate `hovered` via board data-card-id tracking.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────┐   ┌──────────────────────┐
 *   │ DOM event    │ → │ handler (this hook)  │ → │ interaction flags +  │
 *   │ on card root │   │ + host callback      │   │ host side effects    │
 *   └──────────────┘   └──────────────────────┘   └──────────────────────┘
 * RELATED:
 *   - ./KanbanCard.tsx (consumer — spreads rootHandlers, reads flags).
 *   - ./kanbanHoverIntent.ts (dwell + scroll-suppress).
 *   - ../utils/cardSelection (MULTI_CARD_MIME / parseDragCardIds).
 *   - ./KanbanEmailAllocationRail (KANBAN_ALLOCATE_EMAIL_MIME).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  DragEvent as ReactDragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from 'react'

import type { Card as KanbanCardModel } from '../types'
import { isTextInputTarget } from '../undo/inputGuard'
import { useIsCoarsePointer } from '../useIsCoarsePointer'
import { useIsPhone } from '../useIsPhone'
import { MULTI_CARD_MIME, parseDragCardIds } from '../utils/cardSelection'
import {
  CHROME_DWELL_MS,
  isKanbanScrollActive,
  subscribeKanbanScrollActive,
} from './kanbanHoverIntent'
import {
  closeKanbanTouchChrome,
  openKanbanTouchChrome,
  toggleKanbanTouchChrome,
  useKanbanTouchChromeOpen,
} from './kanbanTouchChrome'
import { LONG_PRESS_MS, TOUCH_MOVE_CANCEL_PX } from './kanbanTouchGesture'
import { KANBAN_ALLOCATE_EMAIL_MIME } from './KanbanEmailAllocationRail'

// Card-onto-card nesting is intentionally confined to a card's RIGHT
// edge. Dragging over the body/left of a card lets the dragover/drop
// bubble to the lane (the card lands as a SIBLING in that lane); only
// the right-edge "rail" nests it as a sub-card. This stops accidental
// nests when an operator drags a card to another lane and releases over
// an existing card. Operator request 2026-06-22.
export const NEST_EDGE_FRACTION = 0.33
export const NEST_EDGE_MIN_PX = 44
export const NEST_EDGE_MAX_PX = 120

/**
 * isPointerInNestEdgeZone — pure: is `clientX` within the card's
 * right-edge nest band? The band is `width * FRACTION` clamped to
 * [MIN, MAX] px so it stays a comfortable target on both narrow (mobile)
 * and wide cards. Returns false for a zero/negative width (detached
 * node / jsdom) so nesting degrades to a plain lane drop.
 */
export function isPointerInNestEdgeZone(
  clientX: number,
  rect: { right: number; width: number },
): boolean {
  if (!(rect.width > 0)) return false
  const band = Math.max(
    NEST_EDGE_MIN_PX,
    Math.min(rect.width * NEST_EDGE_FRACTION, NEST_EDGE_MAX_PX),
  )
  return clientX >= rect.right - band
}

function dragInNestEdge(e: ReactDragEvent<HTMLDivElement>): boolean {
  return isPointerInNestEdgeZone(
    e.clientX,
    e.currentTarget.getBoundingClientRect(),
  )
}

export interface UseKanbanCardInteractionsArgs {
  card: KanbanCardModel
  onOpen?: (card: KanbanCardModel) => void
  onSelectToggle?: (card: KanbanCardModel, additive: boolean, range?: boolean) => void
  onContextMenu?: (card: KanbanCardModel, anchor: { x: number; y: number }) => void
  onEmailAllocate?: (card: KanbanCardModel, emailId: string) => void | Promise<unknown>
  onDropAsChild?: (
    parent: KanbanCardModel,
    draggedCardId: string,
    draggedCardIds?: string[],
  ) => Promise<unknown> | void
  onDragStart?: (card: KanbanCardModel, e: ReactDragEvent) => void
  onDragEnd?: (card: KanbanCardModel, e: ReactDragEvent) => void
  /** Arm the hover-dwell checklist preview only when a wired checklist API +
   *  actual items exist (mirrors the card's `checklistInlineReady`). */
  checklistInlineReady: boolean
  hasChecklistItems: boolean
}

export interface KanbanCardRootHandlers {
  onClick: (e: ReactMouseEvent<HTMLDivElement>) => void
  onClickCapture: (e: ReactMouseEvent<HTMLDivElement>) => void
  onMouseDownCapture: (e: ReactMouseEvent<HTMLDivElement>) => void
  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void
  onContextMenu: (e: ReactMouseEvent<HTMLDivElement>) => void
  onTouchStart: (e: ReactTouchEvent<HTMLDivElement>) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  onDragStart: (e: ReactDragEvent<HTMLDivElement>) => void
  onDragEnd: (e: ReactDragEvent<HTMLDivElement>) => void
  onDragOver: (e: ReactDragEvent<HTMLDivElement>) => void
  onDragLeave: (e: ReactDragEvent<HTMLDivElement>) => void
  onDrop: (e: ReactDragEvent<HTMLDivElement>) => void
}

export interface UseKanbanCardInteractionsResult {
  /** Immediate pointer-over (hotkey targeting / board hover id). */
  hovered: boolean
  /** Chrome armed after dwell + not scrolling — gates lift / zone / strip. */
  chromeArmed: boolean
  /** This card's quick-action rail is held open by a long-press (tablet).
   *  Always false on a mouse and on phones, so the desktop/phone paths read
   *  exactly as they did before touch support existed. */
  touchChromeOpen: boolean
  dragging: boolean
  emailDropHover: boolean
  nestDropHover: boolean
  justAllocated: boolean
  hoverExpand: boolean
  /** Phone kebab latches the colour / tools rail (never armed by hover). */
  phoneChromeOpen: boolean
  togglePhoneChrome: () => void
  rootHandlers: KanbanCardRootHandlers
}

export function useKanbanCardInteractions({
  card,
  onOpen,
  onSelectToggle,
  onContextMenu,
  onEmailAllocate,
  onDropAsChild,
  onDragStart,
  onDragEnd,
  checklistInlineReady,
  hasChecklistItems,
}: UseKanbanCardInteractionsArgs): UseKanbanCardInteractionsResult {
  const [hovered, setHovered] = useState(false)
  // FEAT-2026-07-24-KANBAN-HOVER-CHROME-DWELL — action zone / strip / lift /
  // note wait for CHROME_DWELL_MS and stay off while the lane scrolls.
  const [chromeArmed, setChromeArmed] = useState(false)
  const chromeDwellTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pointerOverRef = useRef(false)
  const chromeArmedRef = useRef(false)
  chromeArmedRef.current = chromeArmed
  // Hover-dwell auto-expand (FEAT-2026-06-15-KANBAN-CARD-HOVER) —
  // operator: "hover auto expands the card with its action sequences /
  // checklists." Hovering a card for a short beat reveals its checklist
  // items inline, EVEN in compact density. Transient: collapses on leave.
  const [hoverExpand, setHoverExpand] = useState(false)
  const hoverExpandTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Drag-state polish (Stack 9 PR 6): the original card visibly "lifts"
  // while its ghost follows the cursor.
  const [dragging, setDragging] = useState(false)
  // Allocate-drop hover state (qf!2507).
  const [emailDropHover, setEmailDropHover] = useState(false)
  const [nestDropHover, setNestDropHover] = useState(false)
  // Transient success flash after an email is dropped + allocated.
  const [justAllocated, setJustAllocated] = useState(false)
  const allocatedFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §1.7 — long-press timer for the
  // touch context menu. Held at hook scope so touch handlers can cancel
  // each other across renders.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // BUG-2026-06-29-KANBAN-LONGPRESS-DOUBLE-OPEN — a touch long-press opens
  // the context menu, but the browser still synthesises a `click` on
  // touchend (our touch listeners are passive, so we can't preventDefault
  // there). That click hit onClick → onOpen, so the card ALSO opened behind
  // the menu. We flag the long-press and swallow the next click.
  const suppressClickRef = useRef(false)
  const suppressClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Phone kebab owns the colour rail. Hover must not arm it (DevTools
  // iPhone emulation still fires mouseenter and stacked the rail on click).
  // The slot is the same singleton as the tablet hold — one card at a time.

  // FEAT-2026-08-03-KANBAN-TABLET-LONGPRESS-RAIL — a tablet has no hover, so
  // the quick-action rail (revealed by `chromeArmed` on a mouse) was simply
  // unreachable there. The hold gesture that phones use for the context menu is
  // re-pointed at the RAIL on tablets, which is the surface a tablet is missing;
  // phones keep the context menu, because their zone renders a "⋯ Actions" pill
  // instead of a rail and the menu is the only thing a hold can usefully open.
  const isPhone = useIsPhone()
  const isCoarsePointer = useIsCoarsePointer()
  const longPressOpensRail = isCoarsePointer && !isPhone
  const touchChromeOpen = useKanbanTouchChromeOpen(card.id)
  // A card scrolled out of a virtualised lane (or a board tab switch) must not
  // leave the singleton pointing at a card that no longer exists. Guarded by id
  // so unmounting card A cannot close a rail that has since moved to card B.
  useEffect(() => () => closeKanbanTouchChrome(card.id), [card.id])

  const clearChromeDwell = useCallback(() => {
    if (chromeDwellTimer.current) {
      clearTimeout(chromeDwellTimer.current)
      chromeDwellTimer.current = null
    }
  }, [])

  const armChromeAfterDwell = useCallback(() => {
    clearChromeDwell()
    setChromeArmed(false)
    // Phone: hover never opens colour chrome — that is the kebab's job.
    if (isPhone) return
    if (!pointerOverRef.current || isKanbanScrollActive()) return
    chromeDwellTimer.current = setTimeout(() => {
      chromeDwellTimer.current = null
      if (pointerOverRef.current && !isKanbanScrollActive()) {
        setChromeArmed(true)
      }
    }, CHROME_DWELL_MS)
  }, [clearChromeDwell, isPhone])

  const togglePhoneChrome = useCallback(() => {
    setChromeArmed(false)
    toggleKanbanTouchChrome(card.id)
  }, [card.id])

  useEffect(
    () => () => {
      if (hoverExpandTimer.current) clearTimeout(hoverExpandTimer.current)
      clearChromeDwell()
    },
    [clearChromeDwell],
  )
  useEffect(
    () => () => {
      if (allocatedFlashTimer.current) clearTimeout(allocatedFlashTimer.current)
    },
    [],
  )
  useEffect(
    () => () => {
      if (suppressClickTimer.current) clearTimeout(suppressClickTimer.current)
    },
    [],
  )
  // Scroll suppress: cancel mid-dwell chrome while the pointer is travelling.
  // Once chrome is already armed AND the pointer is still on this card, keep
  // it — otherwise the last card in a lane can never reveal its action rail
  // (growing the zone pushes options below the fold; scrolling to reach them
  // used to disarm chrome immediately). On scroll-end, do NOT call
  // armChromeAfterDwell while already armed — that helper clears armed first
  // and would blink the rail off right after a scroll-to-reach gesture.
  useEffect(
    () =>
      subscribeKanbanScrollActive((active) => {
        if (active) {
          clearChromeDwell()
          if (!pointerOverRef.current) setChromeArmed(false)
          return
        }
        if (!pointerOverRef.current || chromeArmedRef.current) return
        armChromeAfterDwell()
      }),
    [armChromeAfterDwell, clearChromeDwell],
  )

  const rootHandlers: KanbanCardRootHandlers = {
    // BUG-2026-06-29-KANBAN-MULTISELECT-OPENS-PANEL — a Cmd/Ctrl/Shift-click
    // must toggle the board selection and NOTHING else. The earlier fix
    // toggled on the root `onMouseDownCapture` but left the open-suppression
    // on the root's BUBBLE `onClick`. When the click landed on an inner
    // element that opens the card itself (and stop-propagates), the root's
    // bubble `onClick` never ran, so nothing suppressed the open: the card
    // opened, and opening clears the selection (onCardOpen → clearSelection)
    // — exactly the "selects, opens the panel, then deselects on its own"
    // the operator saw. The fix: claim the modifier gesture in the CLICK
    // CAPTURE phase on the root and `stopPropagation()`. Capture runs
    // root→target, so this pre-empts every child `onClick` AND cancels the
    // bubble phase — the card can never open, and the toggle fires exactly
    // once regardless of which sub-element was hit.
    onClickCapture: (e) => {
      if (!onSelectToggle) return
      if (!(e.metaKey || e.ctrlKey || e.shiftKey)) return
      e.preventDefault()
      e.stopPropagation()
      // additive stays true for every modifier (back-compat: callers
      // branch on arg 2); the third arg flags a Shift-click so the lane
      // resolves a contiguous range over its ordered cards.
      onSelectToggle(card, true, e.shiftKey)
    },
    // Modifier + mousedown: kill the default (text-selection on Shift-click,
    // native drag init) so the gesture stays a clean select. The actual
    // toggle happens on `onClickCapture` above — NOT here — so a plain drag
    // is unaffected.
    onMouseDownCapture: (e) => {
      if (e.button !== 0) return
      if (!onSelectToggle) return
      if (!(e.metaKey || e.ctrlKey || e.shiftKey)) return
      e.preventDefault()
    },
    onClick: (e) => {
      // A press on the card rail (Allocate, Move, …) must not open the drawer.
      // The rail stops the bubble, but a draggable card can still deliver the
      // click to this root after the browser cancels the button click.
      const target = e.target
      if (target instanceof Element && target.closest('[data-card-action], [data-card-action-zone], [data-kanban-card-quick-popover]')) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      // Swallow the synthetic click the browser fires after a touch
      // long-press (BUG-2026-06-29) so the card drawer doesn't open behind
      // the context menu the long-press just triggered. (Modifier-clicks are
      // handled entirely in onClickCapture and never reach here.)
      if (suppressClickRef.current) {
        suppressClickRef.current = false
        e.preventDefault()
        e.stopPropagation()
        return
      }
      // Click owns the detail surface — dismiss colour chrome first.
      setChromeArmed(false)
      closeKanbanTouchChrome(card.id)
      onOpen?.(card)
    },
    onKeyDown: (e) => {
      // BUG-2026-07-03-KANBAN-SPACE-IN-COMPOSER — Space/Enter is the card's
      // "open" shortcut, but this handler sits on the card ROOT and React
      // bubbles key events up from nested inputs (the thread/comment
      // composer, inline title edit, mention textarea, …). Without this
      // guard the root preventDefault()'d Space while the operator was
      // typing, so the spacebar did nothing in the composer. Ignore key
      // events originating from a text-input target — let them type.
      if (isTextInputTarget(e.target)) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpen?.(card)
      }
    },
    onContextMenu: (e) => {
      e.preventDefault()
      setChromeArmed(false)
      closeKanbanTouchChrome(card.id)
      onContextMenu?.(card, { x: e.clientX, y: e.clientY })
    },
    // MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §3.4 + §1.7 (Phase 4) — long-press.
    // Movement past TOUCH_MOVE_CANCEL_PX cancels, so this never fights an
    // in-flight scroll/swipe; the board's scroll signal closes an already-open
    // rail via kanbanTouchChrome. What the hold OPENS differs by device: the
    // context menu on phones, the quick-action rail on tablets (see
    // `longPressOpensRail`). Timing comes from kanbanTouchGesture so the drag
    // hook cedes the finger at exactly the same instant this fires.
    onTouchStart: (e) => {
      if (!onContextMenu && !longPressOpensRail) return
      const touch = e.touches[0]
      if (!touch) return
      const startX = touch.clientX
      const startY = touch.clientY
      let cancelled = false
      const cancel = () => {
        cancelled = true
        if (timerRef.current) {
          // Bare clearTimeout/setTimeout (not window.*) so the id type
          // matches `ReturnType<typeof setTimeout>` under @types/node.
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        window.removeEventListener('touchmove', onMove)
        window.removeEventListener('touchend', cancel)
        window.removeEventListener('touchcancel', cancel)
      }
      const onMove = (mv: TouchEvent) => {
        const t = mv.touches[0]
        if (!t) return
        const dx = Math.abs(t.clientX - startX)
        const dy = Math.abs(t.clientY - startY)
        if (dx > TOUCH_MOVE_CANCEL_PX || dy > TOUCH_MOVE_CANCEL_PX) cancel()
      }
      timerRef.current = setTimeout(() => {
        if (cancelled) return
        cancel()
        // Arm the click-swallow: the hold has acted, so the touchend click
        // that follows must NOT also open the card behind it. Auto-clear in
        // case no synthetic click arrives (so the next real tap still
        // opens the card).
        suppressClickRef.current = true
        if (suppressClickTimer.current) clearTimeout(suppressClickTimer.current)
        suppressClickTimer.current = setTimeout(() => {
          suppressClickRef.current = false
        }, 700)
        if (longPressOpensRail) {
          // Opening is enough to close any other card's rail — the singleton
          // holds one slot, so "another card closes the previous" is structural
          // rather than something each card has to remember to do.
          openKanbanTouchChrome(card.id)
          return
        }
        onContextMenu?.(card, { x: startX, y: startY })
      }, LONG_PRESS_MS)
      window.addEventListener('touchmove', onMove, { passive: true })
      window.addEventListener('touchend', cancel, { passive: true })
      window.addEventListener('touchcancel', cancel, { passive: true })
    },
    onMouseEnter: () => {
      pointerOverRef.current = true
      setHovered(true)
      armChromeAfterDwell()
      // Dwell before expanding so a quick pass-over while scanning the
      // board doesn't reflow every lane. Only arm for cards that
      // actually have checklist items + a wired checklist API, and
      // never mid-drag.
      if (checklistInlineReady && hasChecklistItems && !dragging) {
        if (hoverExpandTimer.current) clearTimeout(hoverExpandTimer.current)
        hoverExpandTimer.current = setTimeout(() => setHoverExpand(true), 350)
      }
    },
    onMouseLeave: () => {
      pointerOverRef.current = false
      setHovered(false)
      clearChromeDwell()
      setChromeArmed(false)
      if (hoverExpandTimer.current) {
        clearTimeout(hoverExpandTimer.current)
        hoverExpandTimer.current = null
      }
      setHoverExpand(false)
    },
    onDragStart: (e) => {
      setDragging(true)
      clearChromeDwell()
      setChromeArmed(false)
      // A card being dragged is not a card being acted on; leaving the rail up
      // would float it over the board for the whole drag.
      closeKanbanTouchChrome(card.id)
      onDragStart?.(card, e)
    },
    onDragEnd: (e) => {
      setDragging(false)
      onDragEnd?.(card, e)
    },
    onDragOver: (e) => {
      // Two card-level drop targets:
      //   1. Email-allocate drags from the email rail (always handled
      //      via `text/x-kanban-allocate-email-id`).
      //   2. Stack 12 MR-C — card-onto-card NEST drags (`text/x-kanban-
      //      card-id`). Only when `onDropAsChild` is wired AND the
      //      dragged card isn't this card itself. We swallow the
      //      dragOver so the lane's reorder handler doesn't also fire.
      const types = e.dataTransfer.types
      if (onEmailAllocate && types.includes(KANBAN_ALLOCATE_EMAIL_MIME)) {
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'link'
        if (!emailDropHover) setEmailDropHover(true)
        return
      }
      if (onDropAsChild && types.includes('text/x-kanban-card-id')) {
        // Nest ONLY over the card's right-edge band. Elsewhere we DON'T
        // swallow the event, so it bubbles to the lane's dragover
        // (sibling drop into the lane). The dragged card's id isn't
        // visible during dragover, so self-drops are filtered at drop.
        if (!dragInNestEdge(e)) {
          if (nestDropHover) setNestDropHover(false)
          return
        }
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'move'
        if (!nestDropHover) setNestDropHover(true)
        return
      }
    },
    onDragLeave: (e) => {
      // Filter out child enter events — without this, dragLeave
      // fires whenever the cursor crosses the card's inner
      // elements, producing a strobing border.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      if (emailDropHover) setEmailDropHover(false)
      if (nestDropHover) setNestDropHover(false)
    },
    onDrop: (e) => {
      if (onEmailAllocate) {
        const emailId = e.dataTransfer.getData(KANBAN_ALLOCATE_EMAIL_MIME)
        if (emailId) {
          e.preventDefault()
          e.stopPropagation()
          setEmailDropHover(false)
          // Flash an emerald "✓ Allocated" confirmation once the
          // allocate call resolves (or immediately for void returns).
          Promise.resolve(onEmailAllocate(card, emailId))
            .then(() => {
              setJustAllocated(true)
              if (allocatedFlashTimer.current) clearTimeout(allocatedFlashTimer.current)
              allocatedFlashTimer.current = setTimeout(() => setJustAllocated(false), 1400)
            })
            .catch(() => {
              /* host surfaces the error toast; no flash on failure */
            })
          return
        }
      }
      if (onDropAsChild) {
        const draggedId = e.dataTransfer.getData('text/x-kanban-card-id')
        // FEAT-2026-06-15-KANBAN-MULTISELECT — a multi-selection drag
        // stamps the full id list under MULTI_CARD_MIME; fall back to
        // the single id for a normal drag. Drop the parent itself so a
        // selected card never tries to nest under itself.
        const ids = parseDragCardIds(
          e.dataTransfer.getData(MULTI_CARD_MIME),
          draggedId,
        ).filter((id) => id !== card.id)
        // Nest ONLY when released over the right-edge band; otherwise let
        // the drop bubble to the lane so the card lands as a sibling.
        if (ids.length > 0 && dragInNestEdge(e)) {
          e.preventDefault()
          e.stopPropagation()
          setNestDropHover(false)
          void onDropAsChild(card, ids[0], ids)
          return
        }
        if (nestDropHover) setNestDropHover(false)
      }
    },
  }

  return {
    hovered,
    chromeArmed,
    touchChromeOpen,
    dragging,
    emailDropHover,
    nestDropHover,
    justAllocated,
    hoverExpand,
    phoneChromeOpen: isPhone && touchChromeOpen,
    togglePhoneChrome,
    rootHandlers,
  }
}
